import { useCallback, useState, useEffect } from 'react'
import { gql, useQuery } from '@apollo/client'
import { env } from '~/env.ts'
const isProd = env.VITE_ENV === 'prod'

export const getUserCache = (): User | null => {
  try {
    const cached = localStorage.getItem('mbue_cache_user_data')
    if (!cached) return null
    const entry = JSON.parse(cached) as { data: User; timestamp: number; version: string }
    // Check if cache is expired (10 minutes)
    if (Date.now() - entry.timestamp > 10 * 60 * 1000) {
      localStorage.removeItem('mbue_cache_user_data')
      return null
    }
    return entry.data
  } catch (error) {
    if (!isProd) console.error('Failed to get user cache:', error)
    return null
  }
}

const setUserCache = (data: User) => {
  try {
    const entry = {
      data,
      timestamp: Date.now(),
      version: '1.0.0'
    }
    localStorage.setItem('mbue_cache_user_data', JSON.stringify(entry))
  } catch (error) {
    if (!isProd) console.error('Failed to set user cache:', error)
  }
}

const removeUserCache = () => {
  try {
    localStorage.removeItem('mbue_cache_user_data')
  } catch (error) {
    if (!isProd) console.error('Failed to remove user cache:', error)
  }
}

const hasValidUserCache = (): boolean => {
  return getUserCache() !== null
}

const getUserCacheAge = (): number | null => {
  try {
    const cached = localStorage.getItem('mbue_cache_user_data')
    if (!cached) return null
    const entry = JSON.parse(cached) as { data: User; timestamp: number; version: string }
    return Date.now() - entry.timestamp
  } catch (error) {
    return null
  }
}

export interface UserPlan {
  name: string
}

export interface UserOrg {
  id: number
  name: string
  type: string
  role: string
  featureFlagRBAC: boolean
}

export interface User {
  id: number
  firstName: string
  lastName: string
  email: string
  trial: boolean
  isAdmin: boolean
  emailList: boolean
  profileRole: string
  authenticatedFrom: string
  plan: UserPlan
  orgs: UserOrg[]
  provider: string
  matchedOrg: {
    id: number
    name: string
    projectCount: number
    owner: string
  }
}

const USER_QUERY = gql`
  query MeQuery {
    me {
      id
      firstName
      lastName
      email
      trial
      isAdmin
      emailList
      profileRole
      authenticatedFrom
      provider
      plan {
        name
      }
      orgs {
        id
        name
        type
        role
        featureFlagRBAC
      }
      matchedOrg {
        id
        name
        projectCount
        owner
      }
    }
  }
`

export function useUser() {
  const [cachedUserData, setCachedUserData] = useState<User | null>(null)
  const [isLoadingFromCache, setIsLoadingFromCache] = useState(true)
  const [cacheError, setCacheError] = useState<Error | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    const loadCachedData = () => {
      try {
        const cached = getUserCache()
        if (cached) {
          setCachedUserData(cached)
          setCacheError(null)
          if (!isProd) console.log('Loaded user data from cache')
        }
      } catch (error) {
        if (!isProd) console.error('Failed to load cached user data:', error)
        setCacheError(error instanceof Error ? error : new Error('Unknown cache error'))
      } finally {
        setIsLoadingFromCache(false)
      }
    }

    loadCachedData()
  }, [])

  const { data, loading, error, refetch } = useQuery<{ me: User | null }>(USER_QUERY, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: isLoadingFromCache,
    onCompleted: (newData) => {
      if (newData?.me) {
        try {
          setUserCache(newData.me)
          setCachedUserData(newData.me)
          setCacheError(null)
          setIsRefreshing(false)
          if (!isProd) console.log('Updated user data from network')
        } catch (error) {
          if (!isProd) console.error('Failed to update user cache:', error)
        }
      }
    },
    onError: (error) => {
      if (!isProd) console.error('User data query error:', error)
      setCacheError(error)
      setIsRefreshing(false)
    }
  })

  const refetchUserData = useCallback(async () => {
    try {
      setIsRefreshing(true)
      const result = await refetch()
      if (result.data?.me) {
        setUserCache(result.data.me)
        setCachedUserData(result.data.me)
        setCacheError(null)
      }
      return result
    } catch (error) {
      if (!isProd) console.error('Failed to refetch user data:', error)
      setCacheError(error as Error)
      throw error
    } finally {
      setIsRefreshing(false)
    }
  }, [refetch])

  const refetchFromNetwork = useCallback(async () => {
    try {
      setIsRefreshing(true)
      const result = await refetch({
        fetchPolicy: 'network-only'
      })
      if (result.data?.me) {
        setUserCache(result.data.me)
        setCachedUserData(result.data.me)
        setCacheError(null)
      }
      return result
    } catch (error) {
      if (!isProd) console.error('Failed to refetch user data from network:', error)
      setCacheError(error as Error)
      throw error
    } finally {
      setIsRefreshing(false)
    }
  }, [refetch])

  const invalidateCache = useCallback(async () => {
    try {
      removeUserCache()
      setCachedUserData(null)
      setCacheError(null)
      await refetchUserData()
    } catch (error) {
      if (!isProd) console.error('Failed to invalidate user cache:', error)
      throw error
    }
  }, [refetchUserData])

  const clearCache = useCallback(() => {
    try {
      removeUserCache()
      setCachedUserData(null)
      setCacheError(null)
    } catch (error) {
      if (!isProd) console.error('Failed to clear user cache:', error)
    }
  }, [])

  const isCached = useCallback((): boolean => {
    try {
      return hasValidUserCache()
    } catch (error) {
      if (!isProd) console.error('Failed to check cache status:', error)
      return false
    }
  }, [])

  const getCacheAge = useCallback((): number | null => {
    try {
      return getUserCacheAge()
    } catch (error) {
      if (!isProd) console.error('Failed to get cache age:', error)
      return null
    }
  }, [])

  const currentData = cachedUserData ?? data?.me
  const currentLoading = isLoadingFromCache || (loading && !cachedUserData)
  const currentError = cacheError ?? error

  return {
    user: currentData,
    loading: currentLoading,
    error: currentError,
    refetchUserData,
    refetchFromNetwork,
    invalidateCache,
    clearCache,
    isCached,
    getCacheAge,
    isUsingCache: !!cachedUserData && !data?.me,
    isRefreshing,
    cacheAge: getCacheAge(),
    dataSource: cachedUserData
      ? data?.me
        ? 'cache-then-network'
        : 'cache-only'
      : 'network-only'
  }
}
